/**
 * BookVisitModal.jsx
 *
 * Opens from a projected Forecast row and creates a real scheduled task
 * with the full visit context (visit number, program name, step snapshot,
 * estimated duration) carried through.
 *
 * Status: 'scheduled' (matches calendar scheduler convention for jobs with a set date/time).
 * Lead stage: advanced to 'Booked' on creation (matches SchedulerShell.jsx behaviour).
 *
 * Duplicate guard:
 *   Before writing, queries Firestore for any non-completed task with the same
 *   leadId AND visitNumber — prevents accidental double-booking from the UI.
 */

import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  User,
  RefreshCw,
  Layers,
  FileText,
  Timer,
  AlertCircle,
  Truck,
  Lock,
} from 'lucide-react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { applyRollingRecurrence } from '../utils/applyRollingRecurrence';
import { fetchServiceDefaults, resolveDefaultMode } from '../utils/serviceDefaults';
import { useCatalogItems } from '../hooks/useCatalogItems';
import { buildItemsSnapshot } from '../utils/buildItemsSnapshot';

// ── Helpers ───────────────────────────────────────────────────────────────────

function toLocalInputDate(dateStr) {
  // dateStr is "YYYY-MM-DD" — already local, safe to use as input value
  return dateStr;
}

function buildScheduledEnd(startISO, durationMin) {
  const ms = (durationMin && durationMin > 0 ? durationMin : 60) * 60 * 1000;
  return new Date(new Date(startISO).getTime() + ms).toISOString();
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function BookVisitModal({ visit, lead, companyId, onClose, onBooked }) {
  const [date, setDate]             = useState(toLocalInputDate(visit.projectedDate));
  const [time, setTime]             = useState('09:00');
  const [jobMode, setJobMode]       = useState('route_flexible');
  const [assignedTo, setAssignedTo] = useState(lead?.recurrence?.assignedTo || '');
  const [assignedToName, setAssignedToName] = useState('');
  const [teamMembers, setTeamMembers]       = useState([]);
  const [submitting, setSubmitting]         = useState(false);
  const { byId: catalogById } = useCatalogItems();

  // Derived from visit enrichment
  const { programName, servicePlanStep } = visit;
  const durationMin = servicePlanStep?.estimatedDurationMin || null;
  const stepLabel   = servicePlanStep?.label || null;

  // ── Resolve initial job mode ─────────────────────────────────────────────
  // Priority: program.defaultJobMode (recurring path) → company
  // serviceDefaults[category] (one-time-style fallback) → 'route_flexible'
  // (programs are typically pest/lawn, so route is the safer default here).
  useEffect(() => {
    let cancelled = false;
    async function resolve() {
      // 1. Program default — strongest signal because the operator already
      //    chose how this protocol should be sequenced.
      if (lead?.servicePlanTemplateId) {
        try {
          const progSnap = await getDoc(
            doc(db, 'companies', companyId, 'servicePlanTemplates', lead.servicePlanTemplateId)
          );
          const progMode = progSnap.exists() ? progSnap.data()?.defaultJobMode : null;
          if (!cancelled && (progMode === 'appointment' || progMode === 'route_flexible')) {
            setJobMode(progMode);
            return;
          }
        } catch (_) { /* fall through to category defaults */ }
      }
      // 2. Category defaults
      const defaults = await fetchServiceDefaults(companyId);
      if (cancelled) return;
      const serviceLabel = lead?.service || visit?.serviceLabel || '';
      const resolved = resolveDefaultMode(defaults, serviceLabel, 'route_flexible');
      setJobMode(resolved);
    }
    if (companyId) resolve();
    return () => { cancelled = true; };
  }, [companyId, lead?.servicePlanTemplateId, lead?.service, visit?.serviceLabel]);

  // ── Load team members for assign-to selector ────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    getDocs(collection(db, 'companies', companyId, 'users'))
      .then(snap => {
        setTeamMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      })
      .catch(() => {});
  }, [companyId]);

  // Sync assignedToName when assignedTo changes
  useEffect(() => {
    const member = teamMembers.find(m => m.id === assignedTo || m.uid === assignedTo);
    setAssignedToName(member?.displayName || member?.name || member?.email || '');
  }, [assignedTo, teamMembers]);

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleBook = async () => {
    // Two-mode: route-flexible only needs a date; appointment needs both.
    if (!date) {
      toast.error('Please select a date');
      return;
    }
    if (jobMode === 'appointment' && !time) {
      toast.error('Please select a start time for the appointment');
      return;
    }

    setSubmitting(true);
    try {
      const isRoute = jobMode === 'route_flexible';
      const scheduledStart = isRoute ? `${date}T00:00:00` : `${date}T${time}:00`;
      const scheduledEnd   = isRoute ? null : buildScheduledEnd(scheduledStart, durationMin);

      // ── Duplicate guard ─────────────────────────────────────────────────────
      const tasksRef = collection(db, 'companies', companyId, 'tasks');
      let isDuplicate = false;
      try {
        const dupCheck = await getDocs(
          query(
            tasksRef,
            where('leadId',      '==', visit.leadId),
            where('visitNumber', '==', visit.visitIndex),
            where('status',      '!=', 'completed'),
          )
        );
        isDuplicate = !dupCheck.empty;
      } catch (_) {
        // Composite index may not exist yet — skip guard and proceed with booking
      }

      if (isDuplicate) {
        toast.error(
          `A task already exists for Visit ${visit.visitIndex} of this client.`,
          { description: 'Check the Calendar or Dispatch board.' }
        );
        setSubmitting(false);
        return;
      }

      // ── Freeze catalog items snapshot onto the step ─────────────────────
      // If the caller passed a servicePlanStep that already has `items`,
      // we prefer that. Otherwise we snapshot from `defaultItems` using
      // the tenant catalog map.
      const stepWithItems = servicePlanStep
        ? (Array.isArray(servicePlanStep.items)
            ? servicePlanStep
            : { ...servicePlanStep, items: buildItemsSnapshot(catalogById, servicePlanStep.defaultItems || [], 'program_default') })
        : null;

      // ── Create task ─────────────────────────────────────────────────────────
      await addDoc(tasksRef, {
        leadId:               visit.leadId,
        // Phase 1 lineage: preserve source quote if the forecasted visit carries one.
        ...(visit?.quoteId ? { quoteId: visit.quoteId } : {}),
        title:                lead?.service || visit.serviceLabel || 'Recurring Service',
        clientName:           visit.clientName,
        status:               'scheduled',
        type:                 'job',
        taskCategory:         'job',
        taskType:             'visit',
        // Forward-created service record — explicitly billable by default.
        billingIntent:        'billable',
        serviceType:          lead?.service || '',   // stamp from lead — prevents Unknown in Job Types report
        priority:             'medium',
        scheduledStart,
        scheduledEnd,
        isRecurring:          true,
        recurringServiceId:   visit.leadId,
        seriesIndex:          visit.visitIndex,
        visitNumber:          visit.visitIndex,
        // Program + step snapshot
        servicePlanStep:      stepWithItems     || null,
        servicePlanTemplateId: lead?.servicePlanTemplateId || null,
        programName:          programName       || null,
        // Assignment
        assignedTo:           assignedTo        || null,
        assignedToName:       assignedToName    || null,
        // Job mode — route_flexible (sequenced by Build Day) or appointment (fixed time)
        jobMode:              jobMode,
        hasTime:              jobMode === 'appointment',
        // Metadata
        notes: [
          `Visit ${visit.visitIndex}${visit.totalOccurrences ? ` of ${visit.totalOccurrences}` : ''}`,
          programName ? `Program: ${programName}` : null,
          stepLabel   ? `Step: ${stepLabel}` : null,
        ].filter(Boolean).join(' · '),
        bookedFromForecast:   true,
        createdBy:            auth?.currentUser?.uid || null,
        createdAt:            serverTimestamp(),
        updatedAt:            serverTimestamp(),
      });

      // ── Advance lead stage to Booked ────────────────────────────────────────
      try {
        await updateDoc(
          doc(db, 'companies', companyId, 'leads', visit.leadId),
          { stage: 'Booked' }
        );
      } catch (_) { /* non-blocking */ }

      // ── S1 (stabilization): keep the recurrence cursor in sync with this
      //    newly-booked live task. Without this, recurrence.nextJobDate stays
      //    pointing at the projected date and Service Plan / Outlook end up
      //    disagreeing with the calendar (the original Jason-style desync).
      //    Reuses the same helper Calendar uses on a reschedule drag — no
      //    recurrence-engine change, no new fields.
      if (lead?.recurrence?.active) {
        await applyRollingRecurrence(
          companyId,
          { isRecurring: true, leadId: visit.leadId },
          scheduledStart,
          [lead],
        );
      }

      toast.success(
        `Visit ${visit.visitIndex} booked for ${new Date(`${date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}${jobMode === 'appointment' && time ? ` at ${new Date(`${date}T${time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}` : ''}`,
        { description: programName ? `${programName}${stepLabel ? ` · ${stepLabel}` : ''}` : undefined }
      );

      onBooked?.();
      onClose();
    } catch (err) {
      console.error('[BookVisitModal] Failed to book visit:', err);
      toast.error('Failed to book visit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Book Visit</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Visit context — read only */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 px-3 py-3 space-y-1.5">
            <p className="text-sm font-semibold text-gray-900">{visit.clientName}</p>
            <div className="flex items-center gap-1.5">
              <RefreshCw className="h-3 w-3 text-emerald-500 flex-shrink-0" />
              <span className="text-xs font-medium text-emerald-700">
                Visit {visit.visitIndex}
                {visit.totalOccurrences ? ` of ${visit.totalOccurrences}` : ''}
              </span>
            </div>
            {programName && (
              <div className="flex items-center gap-1.5">
                <Layers className="h-3 w-3 text-gray-400 flex-shrink-0" />
                <span className="text-xs text-gray-600 truncate">{programName}</span>
              </div>
            )}
            {stepLabel && (
              <div className="flex items-center gap-1.5">
                <FileText className="h-3 w-3 text-gray-400 flex-shrink-0" />
                <span className="text-xs text-gray-600 truncate">{stepLabel}</span>
              </div>
            )}
            {durationMin && (
              <div className="flex items-center gap-1.5">
                <Timer className="h-3 w-3 text-gray-400 flex-shrink-0" />
                <span className="text-xs text-gray-500">{durationMin} min estimated</span>
              </div>
            )}
          </div>

          {/* Date + Time */}
          <div className={jobMode === 'appointment' ? 'grid grid-cols-2 gap-3' : ''}>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-500 flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Date
              </label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-400 bg-white"
                data-testid="book-visit-date-input"
              />
            </div>
            {jobMode === 'appointment' && (
              <div className="space-y-1" data-testid="book-visit-time-block">
                <label className="text-xs font-medium text-gray-500 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Start Time
                </label>
                <input
                  type="time"
                  value={time}
                  onChange={e => setTime(e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-400 bg-white"
                  data-testid="book-visit-time-input"
                />
              </div>
            )}
          </div>

          {durationMin && jobMode === 'appointment' && (
            <p className="text-xs text-gray-400 -mt-1">
              End time: {(() => {
                try {
                  const end = new Date(new Date(`${date}T${time}`).getTime() + durationMin * 60000);
                  return end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                } catch { return `+${durationMin} min`; }
              })()}
            </p>
          )}

          {/* Job type — route vs appointment */}
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-gray-500">How is this visit scheduled?</p>
            <div className="grid grid-cols-2 rounded-lg border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setJobMode('route_flexible')}
                className={`flex items-center justify-center gap-1.5 py-2 text-xs font-medium border-r border-gray-200 transition-colors ${
                  jobMode === 'route_flexible'
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
                data-testid="book-visit-mode-route"
              >
                <Truck className="h-3 w-3" />
                Route Stop
              </button>
              <button
                type="button"
                onClick={() => setJobMode('appointment')}
                className={`flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors ${
                  jobMode === 'appointment'
                    ? 'bg-violet-50 text-violet-700'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
                data-testid="book-visit-mode-appointment"
              >
                <Lock className="h-3 w-3" />
                Fixed Appointment
              </button>
            </div>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              {jobMode === 'appointment'
                ? 'Customer receives a specific arrival time.'
                : 'Customer receives a service date only. Dispatch and Build Day determine the route sequence and ETA later.'}
            </p>
          </div>

          {/* Assign to */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1">
              <User className="h-3 w-3" />
              Assign to
            </label>
            <select
              value={assignedTo}
              onChange={e => setAssignedTo(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-400 bg-white"
              data-testid="book-visit-assign-select"
            >
              <option value="">— Unassigned —</option>
              {teamMembers.map(m => {
                const label = m.displayName || m.name || m.email || m.role || `Team member (${m.id.slice(0, 6)}…)`;
                return (
                  <option key={m.id} value={m.id}>{label}</option>
                );
              })}
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleBook}
            disabled={submitting || !date || (jobMode === 'appointment' && !time)}
            className="text-white bg-indigo-600 hover:bg-indigo-700"
            data-testid="book-visit-confirm-btn"
          >
            {submitting ? 'Booking…' : 'Book Visit'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
